import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import locale from 'date-fns/locale/en-US';

const formatDistanceLocale = {
  lessThanXSeconds: '{{count}}s',
  xSeconds: '{{count}}s',
  halfAMinute: '30s',
  lessThanXMinutes: '{{count}}m',
  xMinutes: '{{count}}m',
  aboutXHours: '{{count}}h',
  xHours: '{{count}}h',
  xDays: '{{count}}d',
  aboutXWeeks: '{{count}}w',
  xWeeks: '{{count}}w',
  aboutXMonths: '{{count}}mo',
  xMonths: '{{count}}mo',
  aboutXYears: '{{count}}y',
  xYears: '{{count}}y',
  overXYears: '{{count}}y',
  almostXYears: '{{count}}y',
};

function formatDistanceShortenLocale(token: keyof typeof formatDistanceLocale, count: string) {
  return formatDistanceLocale[token].replace('{{count}}', count);
}

enum Urgency {
  High = 'high',
  Low = 'low',
  Past = 'past',
  None = 'none',
}

const getFutureDistanceTextAndUrgency = (
  futureTimestampMs: number | null | undefined,
): [string | null, Urgency] => {
  /**
   * Null if empty
   */
  if (!futureTimestampMs) {
    return [null, Urgency.None];
  }

  const now = Date.now();

  /**
   * Must be in the _future_
   */
  if (futureTimestampMs < now) {
    return [null, Urgency.Past];
  }

  const urgency = futureTimestampMs - now < 1000 * 60 * 60 * 24 ? Urgency.High : Urgency.Low;
  const result = formatDistanceToNowStrict(futureTimestampMs, {
    addSuffix: true,
    locale: {
      ...locale,
      formatDistance: formatDistanceShortenLocale,
    },
  });
  return [`in ${result}`, urgency];
};

export const useTimeUntil = (
  futureTimestampMs: number | null | undefined,
): [string | null, Urgency] => {
  const [initialText, initialUrgency] = getFutureDistanceTextAndUrgency(futureTimestampMs);
  const [text, setText] = useState(initialText);
  const [urgency, setUrgency] = useState(initialUrgency);
  const interval = useRef<number | null>(null);

  useEffect(() => {
    interval.current = setInterval(() => {
      const [t, u] = getFutureDistanceTextAndUrgency(futureTimestampMs);
      setText(t);
      setUrgency(u);
      if (u === 'past' || u === 'none') {
        clearInterval(interval.current || undefined);
      }
    }, 1000) as unknown as number;
    return () => {
      clearInterval(interval.current || undefined);
    };
  }, [futureTimestampMs]);

  return [text, urgency];
};
